/**
 * The TURN relay for the call (turn-decisions.md T4 to T6).
 *
 * STUN alone did not connect two TAP homes (h0b-result.md), so before the
 * first peer is made the call asks the turn-credentials edge function for
 * short-lived Cloudflare relay servers (purpose "call", the signed-in
 * session rides along in Authorization). One answer is shared by every peer
 * in the call. Anything but a good answer means STUN only: the call still
 * starts and the person never sees the raw error (T5).
 *
 * Near the end of the credentials' life (expiresAt minus 10 minutes) the
 * call asks again and hands the new servers to every open peer with
 * setConfiguration (T4). No iceTransportPolicy 'relay' here: that is a test
 * page flag only (T6).
 */

export const GOOGLE_STUN: RTCIceServer = { urls: 'stun:stun.l.google.com:19302' };
export const TURN_FUNCTION = 'turn-credentials';
export const TURN_TIMEOUT_MS = 5_000;
/** Ask again this long before the credentials run out (T4). */
export const TURN_REFRESH_LEAD_MS = 10 * 60_000;
/** A failed refresh tries again after this, while the old credentials still hold. */
export const TURN_RETRY_MS = 60_000;

export interface TurnGrant {
  iceServers: RTCIceServer[];
  /** Epoch milliseconds. */
  expiresAtMs: number;
}

/** The slice of the Supabase client this needs, so tests can fake it. */
export interface TurnInvoker {
  functions: {
    invoke: (
      name: string,
      options: { body: { purpose: 'call' }; timeout: number },
    ) => Promise<{ data: unknown; error: unknown }>;
  };
}

function isIceServer(value: unknown): value is RTCIceServer {
  if (typeof value !== 'object' || value === null) return false;
  const urls = (value as { urls?: unknown }).urls;
  const list = typeof urls === 'string' ? [urls] : urls;
  return (
    Array.isArray(list) &&
    list.length > 0 &&
    list.every((url) => typeof url === 'string' && /^(stun|turn|turns):/.test(url))
  );
}

/** The function's { iceServers, expiresAt } answer, or null for anything else. */
export function parseTurnAnswer(value: unknown): TurnGrant | null {
  if (typeof value !== 'object' || value === null) return null;
  const { iceServers, expiresAt } = value as { iceServers?: unknown; expiresAt?: unknown };
  if (!Array.isArray(iceServers) || iceServers.length === 0 || !iceServers.every(isIceServer)) {
    return null;
  }
  const expiresAtMs = typeof expiresAt === 'string' ? Date.parse(expiresAt) : Number.NaN;
  if (!Number.isFinite(expiresAtMs)) return null;
  return { iceServers, expiresAtMs };
}

/** Relay credentials for a call, or null (STUN only). Never throws. */
export async function fetchTurn(client: TurnInvoker): Promise<TurnGrant | null> {
  try {
    const { data, error } = await client.functions.invoke(TURN_FUNCTION, {
      body: { purpose: 'call' },
      timeout: TURN_TIMEOUT_MS,
    });
    return error ? null : parseTurnAnswer(data);
  } catch {
    return null;
  }
}

/** The function's servers exactly as returned, then Google STUN as an extra (T6). */
export function iceServersFor(grant: TurnGrant | null): RTCIceServer[] {
  return [...(grant?.iceServers ?? []), GOOGLE_STUN];
}

/** How long until the refresh; never less than a minute, so a short grant cannot spin. */
export function refreshDelayMs(expiresAtMs: number, nowMs: number): number {
  return Math.max(TURN_RETRY_MS, expiresAtMs - TURN_REFRESH_LEAD_MS - nowMs);
}

export interface TurnKeeperDeps {
  fetch: () => Promise<TurnGrant | null>;
  /** Hand new servers to every open peer. */
  apply: (servers: RTCIceServer[]) => void;
  now: () => number;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

/**
 * Keeps one call's relay credentials fresh. Started with the grant the call
 * began with; does nothing for a STUN-only call (no grant, nothing to renew).
 */
export class TurnKeeper {
  private readonly deps: TurnKeeperDeps;
  private timer: unknown = null;
  private expiresAtMs = 0;
  private stopped = false;

  constructor(deps: TurnKeeperDeps) {
    this.deps = deps;
  }

  start(grant: TurnGrant | null): void {
    if (grant === null || this.stopped) return;
    this.expiresAtMs = grant.expiresAtMs;
    this.schedule(refreshDelayMs(grant.expiresAtMs, this.deps.now()));
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) this.deps.clearTimeout(this.timer);
    this.timer = null;
  }

  private schedule(ms: number): void {
    if (this.stopped) return;
    this.timer = this.deps.setTimeout(() => {
      this.timer = null;
      void this.refresh();
    }, ms);
  }

  private async refresh(): Promise<void> {
    if (this.stopped) return;
    const grant = await this.deps.fetch();
    if (this.stopped) return;
    if (grant !== null) {
      this.expiresAtMs = grant.expiresAtMs;
      this.deps.apply(iceServersFor(grant));
      this.schedule(refreshDelayMs(grant.expiresAtMs, this.deps.now()));
      return;
    }
    // Keep the servers we have and try again while they still hold.
    if (this.deps.now() + TURN_RETRY_MS < this.expiresAtMs) this.schedule(TURN_RETRY_MS);
  }
}
